import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandableLineItem from './ExpandableLineItem.jsx';
import { productWarningState } from './productWarningState.js';
import { PRODUCT_STATUS_CONFIG, PRODUCT_STATUS_ORDER, normalizeProductStatus } from './constants.js';
import { Section, StatusChip } from './DrawerPrimitives.jsx';
import { money } from './drawerFormat.js';
import { VARIANCE_SOURCE_LABELS } from './varianceActions.js';
import {
  formatEvidenceDate,
  productCardDate,
  productCardMeta,
  productFamilyLabel,
  renderProductFamilyLanes,
  sortEvidenceProducts,
  productFamilyKey,
} from './DrawerEvidenceHelpers.jsx';
import {
  BILLING_EVIDENCE_DRAG_END,
  BILLING_EVIDENCE_DRAG_START,
  EVIDENCE_DRAG_TYPE,
  hasBillingEvidenceDragType,
} from './billingDragTypes.js';
import {
  adjustedDpeTotalForStatus,
  actionStillAffectsDpePreview,
  dpeActionMatchesStatus,
  dpeActionPrice,
  dpeActionProductCode,
  dpeActionProductName,
  dpeActionSourceStatus,
  dpeActionTargetStatus,
  statusKey,
} from './dpeActionPreviewTotals.js';
import { mergeDpeActionPreviews, previewForDpeAction } from './dpeActionPreviewModel.js';
import { sourceBillability } from './productBillability.js';

const matchesFocusedProduct = (code, focusProductCode) => (
  Boolean(code && focusProductCode && String(code).toUpperCase() === String(focusProductCode).toUpperCase())
);
const testIdPart = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const moneyNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const totalForRows = (rows = [], priceKey) => rows.reduce((sum, row) => sum + moneyNumber(row?.[priceKey]), 0);
const billableStatuses = () => PRODUCT_STATUS_ORDER.filter((status) => PRODUCT_STATUS_CONFIG[status]?.billing);
const totalForBillableStatusRows = (byStatus, priceKey) => billableStatuses()
  .reduce((sum, status) => sum + totalForRows(byStatus.get(status) || [], priceKey), 0);
const adjustedDpeTotalForBillableStatuses = ({ dpeByStatus, dpeActions }) => billableStatuses()
  .reduce((sum, status) => sum + adjustedDpeTotalForStatus({
    baseTotal: totalForRows(dpeByStatus.get(status) || [], 'dealerPrice'),
    dpeActions,
    activeStatus: status,
  }), 0);
const LiveEvidenceColumnHeader = ({ label, labelColor, total, originalTotal = null, action = null }) => {
  const hasAdjustedTotal = originalTotal !== null && Math.round(total) !== Math.round(originalTotal);
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        {action}
        <Typography
          variant="caption"
          data-billing-live-evidence-label={label.toLowerCase()}
          sx={{ color: labelColor, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="baseline" justifyContent="flex-end" sx={{ minWidth: 0 }}>
        {hasAdjustedTotal && (
          <Typography variant="caption" sx={{ color: 'text.disabled', textDecoration: 'line-through', fontWeight: 800 }}>
            {money(originalTotal)}
          </Typography>
        )}
        <Typography
          variant="caption"
          data-billing-live-evidence-total={label.toLowerCase()}
          sx={{ color: hasAdjustedTotal ? 'warning.main' : labelColor, fontWeight: 900 }}
        >
          {money(total)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const DpePanel = ({ items = [], onVarianceMenu, onEvidenceDrop, dense = false, productFamilyOrder = [], productFamilyMinRows = {}, focusProductCode = '', serviceMonth = null, program = 'website' }) => {
  const billing = sortEvidenceProducts(items.filter((product) => product.isBilling));
  const notBilling = sortEvidenceProducts(items.filter((product) => !product.isBilling));
  const renderRow = (product, key, muted = false) => {
    return (
      <ExpandableLineItem
        key={key}
        primary={product.productName || product.productCode}
        secondary={product.productCode}
        meta={productCardMeta({
          code: product.productCode,
          date: productCardDate(product),
        })}
        amount={product.dealerPrice}
        billability={sourceBillability({ source: 'dpe', row: product, status: product.productStatus, serviceMonth, program })}
        muted={muted}
        status={product.productStatus}
        onVarianceMenu={onVarianceMenu}
        onEvidenceDrop={onEvidenceDrop}
        defaultOpen={matchesFocusedProduct(product.productCode, focusProductCode)}
        dense={dense}
        varianceContext={{
          source: 'dpe',
          sourceLabel: VARIANCE_SOURCE_LABELS.dpe,
          productName: product.productName || product.productCode,
          productCode: product.productCode,
          price: Number(product.dealerPrice) || 0,
          status: product.productStatus,
          orderItemId: product.orderItemId,
          referenceId: product.referenceId,
          row: product,
        }}
        details={{
          'Product Code': product.productCode,
          'Product Name': product.productName,
          'Order Item ID': product.orderItemId,
          'Reference ID': product.referenceId,
          'Dealer Price': money(product.dealerPrice),
          'Product Status': product.productStatus,
          'Is Billing': product.isBilling,
          'Enrollment Date': formatEvidenceDate(product.enrollmentDate),
          'Activation Date': formatEvidenceDate(product.activationDate),
          'Cancellation Date': formatEvidenceDate(product.cancellationDate),
          'Effective Date': formatEvidenceDate(product.effectiveDate),
        }}
      />
    );
  };

  return (
    <>
      <Section title="Billing" count={billing.length} countColor={billing.length ? 'success' : 'warning'} emptyText="Not enrolled in DPE">
        {renderProductFamilyLanes({
          rows: billing,
          codeForRow: (product) => product.productCode,
          renderRow,
          keyPrefix: 'dpe-bill',
          familyOrder: productFamilyOrder,
          familyMinRows: productFamilyMinRows,
          showEmptyLanes: productFamilyOrder.length > 1,
          emptyLaneLabel: 'No DPE product',
          dense,
        })}
      </Section>
      <Section title="Not billing" count={notBilling.length} defaultOpen={false} emptyText="No non-billing products">
        {notBilling.map((product, index) => renderRow(product, `dpe-nb-${index}`, true))}
      </Section>
    </>
  );
};

export const AppsheetPanel = ({ data, onVarianceMenu, onEvidenceDrop, dense = false, productFamilyOrder = [], productFamilyMinRows = {}, focusProductCode = '', serviceMonth = null, program = 'website' }) => {
  const [activeStatus, setActiveStatus] = useState('LIVE');
  if (!data) return <Alert severity="info" sx={{ mt: 1 }}>No AppSheet accounts found for this BAC.</Alert>;
  const { orderItems = [] } = data;
  const byStatus = new Map(PRODUCT_STATUS_ORDER.map((status) => [status, []]));
  orderItems.forEach((item) => {
    const bucket = normalizeProductStatus(item.status);
    byStatus.get(bucket).push(item);
  });

  const renderOrderItem = (item, key, muted = false) => {
    const warningRow = {
      ...item,
      orderItemId: item.orderItemId || item.id,
      salesforceAccountId: item.salesforceAccountId || item.accountId,
    };
    return (
    <ExpandableLineItem
      key={key}
      primary={item.productName}
      secondary={item.productCode}
      meta={productCardMeta({
        code: item.productCode,
        date: productCardDate(item),
      })}
      amount={item.mrr}
      billability={sourceBillability({ source: 'appsheet', row: item, status: item.status, serviceMonth, program })}
      muted={muted}
      status={item.status}
      onVarianceMenu={onVarianceMenu}
      onEvidenceDrop={onEvidenceDrop}
      defaultOpen={matchesFocusedProduct(item.productCode, focusProductCode)}
      dense={dense}
      warningState={productWarningState({ row: warningRow })}
      hideWarningLane
      varianceContext={{
        source: 'appsheet',
        sourceLabel: VARIANCE_SOURCE_LABELS.appsheet,
        productName: item.productName || item.productCode,
        productCode: item.productCode,
        price: Number(item.mrr) || 0,
        status: item.status,
        orderItemId: item.id,
        referenceId: item.referenceId,
        row: item,
      }}
      details={{
        'Order Item ID': item.id,
        'Reference ID': item.referenceId,
        'Account ID': item.accountId,
        'Product Name': item.productName,
        'Product Code': item.productCode,
        'Invoice Group': item.invoiceGroup,
        Program: item.programName,
        Status: item.status,
        MRR: money(item.mrr),
        Quantity: item.quantity,
        'Effective Date': formatEvidenceDate(item.effectiveDate),
        'Order Start Date': formatEvidenceDate(item.orderStartDate),
        'Order End Date': formatEvidenceDate(item.orderEndDate),
        'Last Modified': formatEvidenceDate(item.lastModified),
        Notes: item.notes,
      }}
    />
    );
  };

  const activeCfg = PRODUCT_STATUS_CONFIG[activeStatus] || PRODUCT_STATUS_CONFIG.LIVE;
  const activeRows = sortEvidenceProducts(byStatus.get(activeStatus) || []);
  const renderStatusRows = () => {
    if (!activeRows.length) {
      return (
        <Alert severity="info" sx={{ mt: 1 }}>
          No {activeCfg.label.toLowerCase()} order items
        </Alert>
      );
    }
    return renderProductFamilyLanes({
      rows: activeRows,
      codeForRow: (item) => item.productCode,
      renderRow: (item, key) => renderOrderItem(item, key, !activeCfg.billing),
      keyPrefix: `as-${activeStatus}`,
      familyOrder: productFamilyOrder,
      familyMinRows: productFamilyMinRows,
      showEmptyLanes: productFamilyOrder.length > 1,
      emptyLaneLabel: activeCfg.billing ? 'No AppSheet product' : `No ${activeCfg.label.toLowerCase()} product`,
      dense,
    });
  };

  return (
    <>
      <Tabs
        value={activeStatus}
        onChange={(_event, value) => setActiveStatus(value)}
        variant="scrollable"
        scrollButtons={false}
        aria-label="AppSheet product status"
        sx={{
          minHeight: 24,
          mt: -0.35,
          mb: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 24,
            px: 0.85,
            py: 0,
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1.1,
            textTransform: 'none',
          },
        }}
      >
        {PRODUCT_STATUS_ORDER.map((status) => {
          const cfg = PRODUCT_STATUS_CONFIG[status];
          const rows = byStatus.get(status) || [];
          return (
            <Tab
              key={status}
              value={status}
              label={`${cfg.label} ${rows.length}`}
              sx={{ color: rows.length ? cfg.border : 'text.secondary' }}
            />
          );
        })}
      </Tabs>
      <Stack spacing={0.75}>
        {renderStatusRows()}
      </Stack>
      {orderItems.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>No AppSheet rows found for this BAC.</Alert>
      )}
    </>
  );
};

const AppSheetStatusTabs = ({ activeStatus, onStatusChange, byStatus, dpeByStatus = new Map() }) => {
  const activeCfg = PRODUCT_STATUS_CONFIG[activeStatus] || PRODUCT_STATUS_CONFIG.LIVE;
  return (
    <Tabs
      value={activeStatus}
      onChange={(_event, value) => onStatusChange(value)}
      variant="fullWidth"
      aria-label="AppSheet product status"
      TabIndicatorProps={{ sx: { bgcolor: activeCfg.border } }}
      sx={{
        minHeight: 36,
        mb: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '& .MuiTab-root': {
          minHeight: 36,
          px: 1,
          py: 0,
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1.2,
          textTransform: 'none',
        },
      }}
    >
      {PRODUCT_STATUS_ORDER.map((status) => {
        const cfg = PRODUCT_STATUS_CONFIG[status];
        const rows = byStatus.get(status) || [];
        const dpeRows = dpeByStatus.get(status) || [];
        const count = Math.max(rows.length, dpeRows.length);
        return (
          <Tab
            key={status}
            value={status}
            label={`${cfg.label} ${count}`}
            sx={{
              color: cfg.border,
              opacity: 1,
              '&.Mui-selected': { color: cfg.border, opacity: 1 },
            }}
          />
        );
      })}
    </Tabs>
  );
};

const EmptyEvidencePlaceholder = ({ emptyText, dense = false }) => (
  <Paper
    variant="outlined"
    data-billing-product-family-placeholder="true"
    sx={{
      minHeight: dense ? 43 : 56,
      borderRadius: 1,
      borderStyle: 'dashed',
      borderColor: 'divider',
      bgcolor: 'action.hover',
      opacity: 0.55,
      display: 'flex',
      alignItems: 'center',
      px: 1,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 800 }}>
      {emptyText}
    </Typography>
  </Paper>
);

const EvidenceGridCell = ({ children, emptyText, onEvidenceDrop = null, dropLabel = '', placeholderCount = 0, dense = false, testId = '' }) => {
  const [isOver, setIsOver] = useState(false);
  const canDrop = Boolean(onEvidenceDrop);
  const handleDrop = (event) => {
    if (!canDrop) return;
    const rawPayload = event.dataTransfer.getData(EVIDENCE_DRAG_TYPE);
    if (!rawPayload) return;
    event.preventDefault();
    event.stopPropagation();
    setIsOver(false);
    try {
      onEvidenceDrop(JSON.parse(rawPayload));
      window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
    } catch {
      // Ignore unrelated drops.
    }
  };
  return (
  <Stack
    spacing={0.5}
    onDragOver={(event) => {
      if (!canDrop || !hasBillingEvidenceDragType(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      setIsOver(true);
    }}
    onDragLeave={() => setIsOver(false)}
    onDrop={handleDrop}
    sx={{
      minWidth: 0,
      outline: isOver ? '2px solid' : 'none',
      outlineColor: 'info.main',
      outlineOffset: 2,
      borderRadius: 1,
    }}
    aria-label={dropLabel}
    data-testid={testId || undefined}
  >
    {children}
    {Array.from({ length: Math.max(React.Children.count(children) ? 0 : 1, placeholderCount) }).map((_, index) => (
      <EmptyEvidencePlaceholder key={`placeholder-${index}`} emptyText={emptyText} dense={dense} />
    ))}
  </Stack>
  );
};

const DpeRemoveDropLane = ({ dragContext, onDrop }) => {
  const [isOver, setIsOver] = useState(false);
  const isDraggingDpe = dragContext?.source === 'dpe';
  const canDrop = Boolean(onDrop);
  if (!isDraggingDpe) return null;
  return (
    <Paper
      variant="outlined"
      data-billing-dpe-remove-drop-zone="true"
      onDragOver={(event) => {
        if (!canDrop || !hasBillingEvidenceDragType(event.dataTransfer)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        setIsOver(isDraggingDpe);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(event) => {
        const rawPayload = event.dataTransfer.getData(EVIDENCE_DRAG_TYPE);
        if (!rawPayload) return;
        event.preventDefault();
        event.stopPropagation();
        setIsOver(false);
        try {
          const payload = JSON.parse(rawPayload);
          if (payload?.source === 'dpe') onDrop(payload);
        } catch (_error) {
          // Ignore malformed drag payloads from outside the billing drawer.
        }
        window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
      }}
      sx={{
        mt: 1,
        minHeight: 72,
        borderRadius: 1,
        borderStyle: 'dashed',
        borderColor: isOver ? 'error.main' : isDraggingDpe ? 'error.light' : 'divider',
        bgcolor: (theme) => (isOver ? `${theme.palette.error.main}1A` : theme.palette.action.hover),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        opacity: isDraggingDpe ? 1 : 0.68,
      }}
    >
      <DeleteOutlineIcon fontSize="small" color="error" />
      <Typography variant="body2" sx={{ fontWeight: 900, color: isOver ? 'error.main' : 'text.secondary' }}>
        Remove from DPE
      </Typography>
    </Paper>
  );
};

export const LiveEvidenceComparisonPanel = ({
  appsheetData,
  dpeItems = [],
  dpeActions = [],
  onVarianceMenu,
  onEvidenceDrop,
  onDpeActionDrop,
  onDpeActionRemoveDrop,
  dense = true,
  productFamilyOrder = [],
  productFamilyMinRows = {},
  focusProductCode = '',
  accountMatchAction = null,
  allowDpeActions = true,
  serviceMonth = null,
  program = 'website',
}) => {
  const [activeStatus, setActiveStatus] = useState('LIVE');
  const [dragContext, setDragContext] = useState(null);
  useEffect(() => {
    const onDragStart = (event) => setDragContext(event.detail || null);
    const onDragEnd = () => setDragContext(null);
    window.addEventListener(BILLING_EVIDENCE_DRAG_START, onDragStart);
    window.addEventListener(BILLING_EVIDENCE_DRAG_END, onDragEnd);
    return () => {
      window.removeEventListener(BILLING_EVIDENCE_DRAG_START, onDragStart);
      window.removeEventListener(BILLING_EVIDENCE_DRAG_END, onDragEnd);
    };
  }, []);
  if (!appsheetData && !dpeItems.length) return <Alert severity="info" sx={{ mt: 1 }}>No live evidence found for this BAC.</Alert>;
  const appsheetItems = appsheetData?.orderItems || [];
  const byStatus = new Map(PRODUCT_STATUS_ORDER.map((status) => [status, []]));
  appsheetItems.forEach((item) => {
    const bucket = normalizeProductStatus(item.status);
    byStatus.get(bucket).push(item);
  });
  const dpeByStatus = new Map(PRODUCT_STATUS_ORDER.map((status) => [status, []]));
  dpeItems.forEach((product) => {
    const bucket = normalizeProductStatus(product.productStatus, product.isBilling ? 'LIVE' : 'CANCELLED');
    dpeByStatus.get(bucket).push(product);
  });
  const activeCfg = PRODUCT_STATUS_CONFIG[activeStatus] || PRODUCT_STATUS_CONFIG.LIVE;
  const activeAppsheetRows = sortEvidenceProducts(byStatus.get(activeStatus) || []);
  const activeDpeRows = sortEvidenceProducts(dpeByStatus.get(activeStatus) || []);
  const activeDpeActionRows = dpeActions
    .filter(actionStillAffectsDpePreview)
    .filter((action) => dpeActionMatchesStatus(action, activeStatus));
  const {
    rows: dpeRowsWithActionPreviews,
    standaloneActions: standaloneDpeActionRows,
  } = mergeDpeActionPreviews({ products: activeDpeRows, actions: activeDpeActionRows });
  const appsheetBillableTotal = totalForBillableStatusRows(byStatus, 'mrr');
  const dpeBillableTotal = totalForBillableStatusRows(dpeByStatus, 'dealerPrice');
  const adjustedDpeBillableTotal = adjustedDpeTotalForBillableStatuses({ dpeByStatus, dpeActions });
  const orderedFamilies = [...new Set([
    ...productFamilyOrder.map(productFamilyKey),
    ...activeAppsheetRows.map((item) => productFamilyKey(item.productCode)),
    ...activeDpeRows.map((item) => productFamilyKey(item.productCode)),
    ...standaloneDpeActionRows.map((action) => productFamilyKey(dpeActionProductCode(action))),
  ])].filter(Boolean);

  const renderAppSheetItem = (item, key) => {
    const warningRow = {
      ...item,
      orderItemId: item.orderItemId || item.id,
      salesforceAccountId: item.salesforceAccountId || item.accountId,
    };
    return (
      <ExpandableLineItem
        key={key}
        primary={item.productName}
        secondary={item.productCode}
        meta={productCardMeta({ code: item.productCode, date: productCardDate(item) })}
        amount={item.mrr}
        billability={sourceBillability({ source: 'appsheet', row: item, status: item.status, serviceMonth, program })}
        muted={!activeCfg.billing}
        status={item.status}
        onVarianceMenu={onVarianceMenu}
        onEvidenceDrop={onEvidenceDrop}
        defaultOpen={matchesFocusedProduct(item.productCode, focusProductCode)}
        dense={dense}
        warningState={productWarningState({ row: warningRow })}
        hideWarningLane
        compactCard
        varianceContext={{
          source: 'appsheet',
          sourceLabel: VARIANCE_SOURCE_LABELS.appsheet,
          productName: item.productName || item.productCode,
          productCode: item.productCode,
          price: Number(item.mrr) || 0,
          status: item.status,
          orderItemId: item.id,
          referenceId: item.referenceId,
          row: item,
        }}
        details={{
          'Order Item ID': item.id,
          'Reference ID': item.referenceId,
          'Account ID': item.accountId,
          'Product Name': item.productName,
          'Product Code': item.productCode,
          'Invoice Group': item.invoiceGroup,
          Program: item.programName,
          Status: item.status,
          MRR: money(item.mrr),
          'Effective Date': formatEvidenceDate(item.effectiveDate),
        }}
      />
    );
  };

  const renderDpeItem = (product, key, action = null) => (
    <ExpandableLineItem
      key={key}
      primary={product.productName || product.productCode}
      secondary={product.productCode}
      meta={productCardMeta({ code: product.productCode, date: productCardDate(product) })}
      amount={product.dealerPrice}
      billability={sourceBillability({ source: 'dpe', row: product, status: product.productStatus, serviceMonth, program })}
      muted={!activeCfg.billing}
      status={product.productStatus}
      onVarianceMenu={onVarianceMenu}
      onEvidenceDrop={onEvidenceDrop}
      defaultOpen={matchesFocusedProduct(product.productCode, focusProductCode)}
      dense={dense}
      compactCard
      draftPreview={action ? { ...previewForDpeAction(action), action } : null}
      varianceContext={{
        source: 'dpe',
        sourceLabel: VARIANCE_SOURCE_LABELS.dpe,
        productName: product.productName || product.productCode,
        productCode: product.productCode,
        price: Number(product.dealerPrice) || 0,
        status: product.productStatus,
        orderItemId: product.orderItemId,
        referenceId: product.referenceId,
        row: product,
      }}
      details={{
        'Product Code': product.productCode,
        'Product Name': product.productName,
        'Order Item ID': product.orderItemId,
        'Reference ID': product.referenceId,
        'Dealer Price': money(product.dealerPrice),
        'Product Status': product.productStatus,
        'Effective Date': formatEvidenceDate(product.effectiveDate),
      }}
    />
  );

  const renderDpeActionPreview = (action, key) => {
    const code = dpeActionProductCode(action);
    const targetStatus = dpeActionTargetStatus(action);
    const sourceStatus = dpeActionSourceStatus(action);
    const preview = previewForDpeAction(action);
    return (
      <ExpandableLineItem
        key={key}
        primary={dpeActionProductName(action)}
        secondary={code}
        meta={productCardMeta({
          code,
          date: action.effectiveDate || action.vmtPayload?.effectiveDate,
        })}
        amount={dpeActionPrice(action)}
        billability={sourceBillability({ source: 'dpe', row: action.vmtPayload || action, status: targetStatus, serviceMonth, program })}
        muted={action.actionType === 'remove' && sourceStatus === statusKey(activeStatus)}
        status={action.actionType === 'remove' ? targetStatus : targetStatus}
        dense={dense}
        compactCard
        onVarianceMenu={onVarianceMenu}
        onEvidenceDrop={onEvidenceDrop}
        defaultOpen={matchesFocusedProduct(code, focusProductCode)}
        draftPreview={{
          label: preview.label,
          variant: preview.variant,
          action,
        }}
        varianceContext={{
          source: 'dpe',
          sourceLabel: VARIANCE_SOURCE_LABELS.dpe,
          productName: dpeActionProductName(action),
          productCode: code,
          price: dpeActionPrice(action),
          status: targetStatus,
          referenceId: action.referenceId || action.vmtPayload?.referenceId,
          row: action,
        }}
        details={{
          'DPE Action': preview.label,
          'Queue Status': action.queueStatus,
          'Product Code': code,
          'Product Name': dpeActionProductName(action),
          'Reference ID': action.referenceId || action.vmtPayload?.referenceId,
          'Dealer Price': money(dpeActionPrice(action)),
          'From Status': sourceStatus,
          'To Status': targetStatus,
          'Effective Date': formatEvidenceDate(action.effectiveDate || action.vmtPayload?.effectiveDate),
        }}
      />
    );
  };

  return (
    <Stack spacing={0}>
      <AppSheetStatusTabs activeStatus={activeStatus} onStatusChange={setActiveStatus} byStatus={byStatus} dpeByStatus={dpeByStatus} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          columnGap: 1,
          alignItems: 'center',
          px: 0.25,
          mt: 1.5,
          pb: 0.25,
        }}
      >
        <LiveEvidenceColumnHeader label="AppSheet" labelColor="success.main" total={appsheetBillableTotal} action={accountMatchAction} />
        <LiveEvidenceColumnHeader label="DPE" labelColor="info.main" total={adjustedDpeBillableTotal} originalTotal={dpeBillableTotal} />
      </Box>
      <Stack spacing={0.75} sx={{ pt: '4px', position: 'relative', top: '-6px', mb: '-6px' }}>
        {orderedFamilies.map((family) => {
          const appsheetRows = activeAppsheetRows.filter((item) => productFamilyKey(item.productCode) === family);
          const dpeRows = dpeRowsWithActionPreviews.filter(({ product }) => productFamilyKey(product.productCode) === family);
          const dpeActionRows = standaloneDpeActionRows.filter((action) => productFamilyKey(dpeActionProductCode(action)) === family);
          const dpeRowCount = dpeRows.length + dpeActionRows.length;
          const reservedRows = Math.max(
            Number(productFamilyMinRows[family]) || 0,
            appsheetRows.length,
            dpeRowCount,
            1,
          );
          return (
            <Box key={family} data-billing-live-evidence-family={family} sx={{ minHeight: dense ? 82 : 95 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 900, display: 'block', pl: 0.5, pb: 0.25 }}
              >
                {productFamilyLabel(family)}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                  gap: 0.75,
                  minHeight: dense ? 48 : 61,
                }}
              >
                <EvidenceGridCell
                  emptyText={activeCfg.billing ? 'No AppSheet product' : `No ${activeCfg.label.toLowerCase()} product`}
                  placeholderCount={reservedRows - appsheetRows.length}
                  dense={dense}
                >
                  {appsheetRows.map((item, index) => renderAppSheetItem(item, `as-grid-${family}-${index}`))}
                </EvidenceGridCell>
                <EvidenceGridCell
                  emptyText="No DPE product"
                  dropLabel={allowDpeActions ? 'Drop AppSheet product to queue a DPE action' : null}
                  testId={`billing-dpe-drop-${testIdPart(family)}-${testIdPart(activeStatus)}`}
                  onEvidenceDrop={allowDpeActions && onDpeActionDrop ? (sourceContext) => onDpeActionDrop(sourceContext, { source: 'dpe', family, status: activeStatus }) : null}
                  placeholderCount={reservedRows - dpeRowCount}
                  dense={dense}
                >
                  {dpeRows.map(({ product, action }, index) => renderDpeItem(product, `dpe-grid-${family}-${index}`, action))}
                  {dpeActionRows.map((action, index) => renderDpeActionPreview(action, `dpe-action-grid-${family}-${index}`))}
                </EvidenceGridCell>
              </Box>
            </Box>
          );
        })}
        {allowDpeActions ? <DpeRemoveDropLane dragContext={dragContext} onDrop={onDpeActionRemoveDrop} /> : null}
      </Stack>
    </Stack>
  );
};

export const TradePanel = ({ data }) => {
  if (!data) return <Alert severity="info" sx={{ mt: 1 }}>Dealer is not enrolled in the Trade-In program.</Alert>;
  const { enrollment, recentActivity = [] } = data;
  const statusCfg = PRODUCT_STATUS_CONFIG[enrollment.status];
  const isBilling = statusCfg?.billing;

  return (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 1.5, borderLeft: statusCfg?.border ? `4px solid ${statusCfg.border}` : undefined }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{enrollment.tier}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{enrollment.contractTerm} - go live {enrollment.goLiveDate}</Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <StatusChip status={enrollment.status} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: isBilling ? 'inherit' : 'text.disabled' }}>
                {isBilling ? money(enrollment.monthlyCost) : '--'}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(130px, auto) 1fr', columnGap: 1.5, rowGap: 0.25 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>Go Live Date</Typography>
            <Typography variant="caption">{enrollment.goLiveDate || '--'}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>Last Updated</Typography>
            <Typography variant="caption">{enrollment.lastUpdated || '--'}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>Contract Term</Typography>
            <Typography variant="caption">{enrollment.contractTerm || '--'}</Typography>
            {enrollment.cancelDate && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>Cancel Date</Typography>
                <Typography variant="caption">{enrollment.cancelDate}</Typography>
              </>
            )}
          </Box>
        </Stack>
      </Paper>
      <Section title="Recent activity" count={recentActivity.length} defaultOpen={recentActivity.length > 0} emptyText="No appraisal activity in the trailing window">
        {recentActivity.map((month) => (
          <Stack key={month.month} direction="row" alignItems="center" spacing={1} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, width: 36 }}>{month.month}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {month.appraisals} appraisals - {month.offers} offers - {month.acceptedOffers} accepted
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {Math.round((month.acceptedOffers / Math.max(1, month.appraisals)) * 100)}%
            </Typography>
          </Stack>
        ))}
      </Section>
    </Stack>
  );
};
